import { useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { WAREHOUSE_IMAGES_URL } from '@constants';
import { Dropzone } from '../../tailwindComponents/Dropzone';
import { Image } from './types';

const buildImagePreviewSrc = (image: Image) => {
  return `${WAREHOUSE_IMAGES_URL}/${image.imageLocation}/small/${image.remoteName}`;
};

export function ImageField({
  label,
  dropzoneId,
  existingImage,
  stagedFile,
  onStage,
  onRemoveExisting,
  squareCrop,
  compact,
}: {
  label: string;
  dropzoneId: string;
  existingImage: Image | null;
  stagedFile: unknown;
  onStage: (file: File | null) => void;
  onRemoveExisting: (existingImageId: string) => void;
  squareCrop?: boolean;
  compact?: boolean;
}) {
  const [failedImageId, setFailedImageId] = useState<string | null>(null);

  if (existingImage?.id) {
    const hasFailedToLoad = failedImageId === existingImage.id;
    return (
      <div className='flex flex-col space-y-2'>
        <div className='block text-sm leading-5 font-medium text-gray-900 font-inter'>
          {label}
          <span className='text-sm leading-5 font-normal font-inter text-gray-400 ml-2'>
            Optional
          </span>
        </div>
        <div className='relative'>
          {hasFailedToLoad ? (
            <div
              className={`flex w-full flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 bg-gray-100 ${
                compact ? 'h-36' : 'h-60'
              }`}
            >
              <PhotoIcon className='h-8 w-8 text-gray-300' />
              <p className='max-w-full truncate px-4 font-inter text-sm text-gray-500'>
                {existingImage.title || label}
              </p>
              <p className='font-inter text-xs text-gray-400'>
                Preview unavailable
              </p>
            </div>
          ) : (
            <img
              src={buildImagePreviewSrc(existingImage)}
              alt={existingImage.title || label}
              onError={() => setFailedImageId(existingImage.id)}
              className={`object-contain ${
                compact ? 'max-h-36' : 'max-h-60'
              } max-w-full rounded`}
            />
          )}
          <button
            type='button'
            onClick={() => onRemoveExisting(existingImage.id)}
            aria-label={`Delete ${label}`}
            className='absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition'
          >
            <MdDelete size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <Dropzone
      dropzoneId={dropzoneId}
      label={label}
      optional
      enableImageEditing
      imageCropAspect={squareCrop ? 1 : undefined}
      value={stagedFile instanceof File ? stagedFile : null}
      onDrop={onStage}
      compact={compact}
    />
  );
}
